import { makeAutoObservable } from "mobx";
import Storage from "utils/storage";
import { get_currencies, get_currencyInfoOne, get_priceCurrencyConvert } from "api/v4/balance";

export interface CurrencyProps extends ObjAny {
  id: number;
  currency: string;
  fullName: string;
  logo?: string;
  cmcLink?: string;
  weight: number;
  maxPrecision: number;
  depositStatus: 0 | 1;
  withdrawStatus: 0 | 1;
  convertEnabled: 0 | 1;
  transferEnabled: 0 | 1;
}

interface CurrencyInfoProps extends ObjAny {
  currency: string;
  fullName: string;
  publishTime: number;
  tokenSummary: string | null;
}

interface PriceCurrencyConvertProps extends ObjAny {
  btc: string;
  usd?: string;
  cny?: string;
  inr?: string;
  idr?: string;
}

interface StateProps {
  currencies: WithUndefined<CurrencyProps[]>;
  currencyInfo: WithUndefined<CurrencyInfoProps | null>;
  priceCurrencyConvert: WithUndefined<ObjT<PriceCurrencyConvertProps>>;
}

let timeoutFun;
let cancelFunGetCurrencyInfoOne;
let cancelFunGetPriceCurrencyConvert;

const currency = makeAutoObservable(
  {
    currencies: undefined as StateProps["currencies"],
    currencyInfo: undefined as StateProps["currencyInfo"],
    priceCurrencyConvert: undefined as StateProps["priceCurrencyConvert"],

    get currencyObj(): WithUndefined<ObjT<CurrencyProps>> {
      if (!this.currencies) return;
      const doc: ObjT<CurrencyProps> = {};
      this.currencies.map((obj: any) => (doc[obj.currency] = obj));
      return doc;
    },

    updateState(payload: Partial<StateProps>) {
      for (const key in payload) {
        this[key] = payload[key];
      }
    },

    updateStateOnce(payload: Partial<StateProps>) {
      for (const key in payload) {
        if (!this[key]) this[key] = payload[key];
      }
    },

    getCurrencies(finallyFun?: () => void) {
      const { version, currencies } = Storage.get("currencies") || {};
      this.currencies = currencies;
      get_currencies({ params: { version } })
        .then((data) => {
          if (data.currencies && data.version !== version) {
            this.currencies = data.currencies;
            Storage.set("currencies", data);
          }
        })
        .catch(() => {})
        .finally(finallyFun);
    },

    getCurrencyInfoOne(currencyCode) {
      this.currencyInfo = undefined;
      try {
        cancelFunGetCurrencyInfoOne && cancelFunGetCurrencyInfoOne();
      } catch (e) {
        cancelFunGetCurrencyInfoOne = null;
      }
      get_currencyInfoOne(currencyCode, {
        cancelFun: (c) => (cancelFunGetCurrencyInfoOne = c),
      })
        .then((data) => {
          this.currencyInfo = data;
        })
        .catch(() => {
          this.currencyInfo = null;
        })
        .finally(() => {
          cancelFunGetCurrencyInfoOne = null;
        });
    },

    loopGetPriceCurrencyConvert(currencyCode: string) {
      clearTimeout(timeoutFun);
      try {
        cancelFunGetPriceCurrencyConvert && cancelFunGetPriceCurrencyConvert();
      } catch (e) {
        cancelFunGetPriceCurrencyConvert = null;
      }

      get_priceCurrencyConvert({
        params: {
          converts: currencyCode === "btc" ? currencyCode : currencyCode + ",btc",
        },
        cancelFun: (c) => (cancelFunGetPriceCurrencyConvert = c),
      })
        .then((data) => {
          this.priceCurrencyConvert = data;
        })
        .catch(() => {})
        .finally(() => {
          cancelFunGetPriceCurrencyConvert = null;
          clearTimeout(timeoutFun);
          timeoutFun = setTimeout(() => {
            this.loopGetPriceCurrencyConvert(currencyCode);
          }, 3e4);
        });
    },

    getCurrencyObjByMarketName(marketName: string) {
      return this.currencies?.find((item) => item.currency === marketName.toLocaleLowerCase());
    },
  },
  {},
  {
    autoBind: true,
    deep: false,
  }
);

export default currency;
